import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import Accordion from '@mui/material/Accordion';
import { Button, TextField, Card, Grid, CardHeader, CardContent, IconButton, Tooltip } from '@mui/material';
import { SelectField } from 'components';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import { enqueueSnackbar } from 'actions';
import ModalDialog from '../../../../../components/ModalDialog/ModalDialog.js';
import { SetFormState, GetSiteDropdowns, ToggleOptionsSiteTrackerForm, RemoveDropdownField } from '../../../actions/SiteTrackerActions';
import SiteTrackerOptionsForm from './components/SiteTrackerOptionsForm.js';
import './SiteTrackerOptions.css';

const SiteTrackerOptionsDialog = (props) => {
	const { author, open, onClose, onApply, className, ...rest } = props;
	const dispatch = useDispatch();
	const tableId = useSelector((state) => state.siteTrackerReducer.tableId);
	const userId = useSelector(state => state.session.user.id);
	const role = useSelector(state => state.session.user.role);
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector(state => state.siteTrackerReducer.formState);
	const dropdowns = useSelector(state => state.siteTrackerReducer.dropdowns);
	const optionsSiteTrackerFormVisibility = useSelector(state => state.siteTrackerReducer.optionsSiteTrackerFormVisibility);
	const dropdownLabel = useSelector(state => state.siteTrackerReducer.dropdownLabel);
	const dropdownName = useSelector(state => state.siteTrackerReducer.dropdownName);
	const dropdownValue = useSelector(state => state.siteTrackerReducer.dropdownValue);
	const dropdownId = useSelector(state => state.siteTrackerReducer.dropdownId);

	const newDropdownField = useSelector(state => state.siteTrackerReducer.newDropdownField);

	const [expanded, setExpanded] = useState(false);

	const handleChange = (panel) => (event, isExpanded) => {
		setExpanded(isExpanded ? panel : false);
	};

	const getData = useCallback(() => {
		dispatch(GetSiteDropdowns());
	}, []);

	useEffect(() => {
		getData();
	}, []);

	const _handleChangeDropDown = event => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value
			},
			touched: {
				...formState.touched,
				[event.target.name]: true
			}
		});
	};

	const _handleChangeLevels = event => {
		if (event.target.name === 'levelOne') {
			// Filtrar
			let levelsTwo = dropdowns.levelsRelation
				.filter(x => x.levelOneId === event.target.value)
				.map(({ levelTwoId, levelTwoName }) => ({
					value: levelTwoId,
					text: levelTwoName
				}));
			// Distintos
			levelsTwo = [...new Map(levelsTwo.map((item) => [item.value, item])).values()];

			setFormState({
				...formState,
				values: {
					...formState.values,
					[event.target.name]: event.target.value,
					levelTwo: undefined,
					levelThree: undefined,
					levelsTwo,
					levelsThree: [],
				},
			});
		} else if (event.target.name === 'levelTwo') {
			// Filtrar
			let levelsThree = dropdowns.levelsRelation
				.filter(x => x.levelTwoId === event.target.value)
				.map(({ levelThreeId, levelThreeName }) => ({
					value: levelThreeId,
					text: levelThreeName
				}));
			// Distintos
			levelsThree = [...new Map(levelsThree.map((item) => [item.value, item])).values()];

			setFormState({
				...formState,
				values: {
					...formState.values,
					[event.target.name]: event.target.value,
					levelThree: undefined,
					levelsThree,
				},
			});
		} else if (event.target.name === 'levelThree') {
			setFormState({
				...formState,
				values: {
					...formState.values,
					[event.target.name]: event.target.value
				},
			});
		}
	};

	const _addField = (event, label, name) => {
		dispatch(ToggleOptionsSiteTrackerForm(true, label, name));
	};

	useMemo(() => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[newDropdownField.name]: newDropdownField.id
			}
		});
	}, [newDropdownField]);

	useMemo(() => {
		if (dropdowns && dropdowns.levelsRelation) {
			// Filtrar
			let levelsTwo = dropdowns.levelsRelation
				.filter(x => x.levelOneId === formState.values.levelOne)
				.map(({ levelTwoId, levelTwoName }) => ({
					value: levelTwoId,
					text: levelTwoName
				}));
			// Distintos
			levelsTwo = [...new Map(levelsTwo.map((item) => [item.value, item])).values()];

			// Filtrar
			let levelsThree = dropdowns.levelsRelation
				.filter(x => x.levelTwoId === formState.values.levelTwo)
				.map(({ levelThreeId, levelThreeName }) => ({
					value: levelThreeId,
					text: levelThreeName
				}));
			// Distintos
			levelsThree = [...new Map(levelsThree.map((item) => [item.value, item])).values()];

			setFormState({
				...formState,
				values: {
					...formState.values,
					levelsTwo,
					levelsThree
				}
			});
		}
	}, [dropdowns]);

	const _deleteField = (event, label, name, value, id) => {
		dispatch(ToggleOptionsSiteTrackerForm(true, label, name, value, id));
	};

	return (
		<ModalDialog
			// maxWidth="xl"
			onClose={onClose}
			open={open}
			title="Options"
			// confirmLabel="Ok"
			hasConfirm={false}
			cancelLabel="Close"
		>
			<div
				style={{ marginTop: '10px' }}
			>
				{optionsSiteTrackerFormVisibility && (
					<SiteTrackerOptionsForm
						open={optionsSiteTrackerFormVisibility}
						dropdownLabel={dropdownLabel}
						dropdownName={dropdownName}
						dropdownValue={dropdownValue}
						dropdownId={dropdownId}
					/>
				)}
				<Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel1bh-content"
						id="panel1bh-header"
						className='highlight'
					>
						<Typography sx={{ width: '33%', flexShrink: 0 }}>Edit MOCN Activation Status</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={1}>
							<Grid item md={3} xs={3}>
								<SelectField
									id="mocnActivationStatus"
									name="mocnActivationStatus"
									hintText="MOCN Activation Status"
									defaultValue=""
									menuItems={(dropdowns && dropdowns.mocnActivationStatus) || []}
									value={formState.values.mocnActivationStatus}
									onChange={_handleChangeDropDown}
								/>
							</Grid>
							<Grid item md={3} xs={3} style={{ display: 'flex', alignItems: 'center' }}>
								<Tooltip title="Add">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: '#1565c0' }}
											onClick={(e) => _addField(e, 'MOCN Activation Status', 'mocnActivationStatus')}
										>
											<AddCircleOutlineIcon />
										</IconButton>
									</span>
								</Tooltip>
								<Tooltip title="Remove">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: formState.values.mocnActivationStatus === undefined ? 'grey' : 'red' }}
											disabled={formState.values.mocnActivationStatus === undefined}
											onClick={(e) => _deleteField(
												e,
												'MOCN Activation Status',
												'mocnActivationStatus',
												dropdowns.mocnActivationStatus.find(x => x.value === formState.values.mocnActivationStatus).text,
												formState.values.mocnActivationStatus
											)}
										>
											<CancelIcon />
										</IconButton>
									</span>
								</Tooltip>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
				<Accordion expanded={expanded === 'panel2'} onChange={handleChange('panel2')}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel2bh-content"
						id="panel2bh-header"
						className='highlight'
					>
						<Typography sx={{ width: '33%', flexShrink: 0 }}>Edit Cluster Use Case</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={1}>
							<Grid item md={3} xs={3}>
								<SelectField
									id="clusterUseCase"
									name="clusterUseCase"
									hintText="Cluster Use Case"
									defaultValue=""
									menuItems={(dropdowns && dropdowns.clusterUseCase) || []}
									value={formState.values.project || ''}
									onChange={_handleChangeDropDown}
								/>
							</Grid>
							<Grid item md={3} xs={3} style={{ display: 'flex', alignItems: 'center' }}>
								<Tooltip title="Add">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: '#1565c0' }}
											onClick={(e) => _addField(e, 'Cluster Use Case', 'clusterUseCase')}
										>
											<AddCircleOutlineIcon />
										</IconButton>
									</span>
								</Tooltip>
								<Tooltip title="Remove">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: formState.values.clusterUseCase === undefined ? 'grey' : 'red' }}
											disabled={formState.values.clusterUseCase === undefined}
											onClick={(e) => _deleteField(
												e,
												'Cluster Use Case',
												'clusterUseCase',
												dropdowns.clusterUseCase.find(x => x.value === formState.values.clusterUseCase).text,
												formState.values.clusterUseCase
											)}
										>
											<CancelIcon />
										</IconButton>
									</span>
								</Tooltip>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
				<Accordion expanded={expanded === 'panel3'} onChange={handleChange('panel3')}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel3bh-content"
						id="panel3bh-header"
						className='highlight'
					>
						<Typography sx={{ width: '33%', flexShrink: 0 }}>Edit Benefit</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={1}>
							<Grid item md={3} xs={3}>
								<SelectField
									id="benefit"
									name="benefit"
									hintText="Benefit"
									defaultValue=""
									menuItems={(dropdowns && dropdowns.benefit) || []}
									value={formState.values.benefit || ''}
									onChange={_handleChangeDropDown}
								/>
							</Grid>
							<Grid item md={3} xs={3} style={{ display: 'flex', alignItems: 'center' }}>
								<Tooltip title="Add">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: '#1565c0' }}
											onClick={(e) => _addField(e, 'Benefit', 'benefit')}
										>
											<AddCircleOutlineIcon />
										</IconButton>
									</span>
								</Tooltip>
								<Tooltip title="Remove">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: formState.values.benefit === undefined ? 'grey' : 'red' }}
											disabled={formState.values.benefit === undefined}
											onClick={(e) => _deleteField(
												e,
												'Benefit',
												'benefit',
												dropdowns.benefit.find(x => x.value === formState.values.benefit).text,
												formState.values.benefit
											)}
										>
											<CancelIcon />
										</IconButton>
									</span>
								</Tooltip>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
				<Accordion expanded={expanded === 'panel4'} onChange={handleChange('panel4')}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel4bh-content"
						id="panel4bh-header"
						className='highlight'
					>
						<Typography sx={{ width: '33%', flexShrink: 0 }}>Edit E-MOCN Type</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={1}>
							<Grid item md={3} xs={3}>
								<SelectField
									id="eMocnType"
									name="eMocnType"
									hintText="E-MOCN Type"
									defaultValue=""
									menuItems={(dropdowns && dropdowns.eMocnType) || []}
									value={formState.values.eMocnType || ''}
									onChange={_handleChangeDropDown}
								/>
							</Grid>
							<Grid item md={3} xs={3} style={{ display: 'flex', alignItems: 'center' }}>
								<Tooltip title="Add">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: '#1565c0' }}
											onClick={(e) => _addField(e, 'E-MOCN Type', 'eMocnType')}
										>
											<AddCircleOutlineIcon />
										</IconButton>
									</span>
								</Tooltip>
								<Tooltip title="Remove">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: formState.values.eMocnType === undefined ? 'grey' : 'red' }}
											disabled={formState.values.eMocnType === undefined}
											onClick={(e) => _deleteField(
												e,
												'E-MOCN Type',
												'eMocnType',
												dropdowns.eMocnType.find(x => x.value === formState.values.eMocnType).text,
												formState.values.eMocnType
											)}
										>
											<CancelIcon />
										</IconButton>
									</span>
								</Tooltip>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
				<Accordion expanded={expanded === 'panel5'} onChange={handleChange('panel5')}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel5bh-content"
						id="panel5bh-header"
						className='highlight'
					>
						<Typography sx={{ width: '33%', flexShrink: 0 }}>Edit Beacon</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={1}>
							<Grid item md={3} xs={3}>
								<SelectField
									id="beacon"
									name="beacon"
									hintText="Beacon"
									defaultValue=""
									menuItems={(dropdowns && dropdowns.beacon) || []}
									value={formState.values.beacon || ''}
									onChange={_handleChangeDropDown}
								/>
							</Grid>
							<Grid item md={3} xs={3} style={{ display: 'flex', alignItems: 'center' }}>
								<Tooltip title="Add">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: '#1565c0' }}
											onClick={(e) => _addField(e, 'Beacon', 'beacon')}
										>
											<AddCircleOutlineIcon />
										</IconButton>
									</span>
								</Tooltip>
								<Tooltip title="Remove">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: formState.values.beacon === undefined ? 'grey' : 'red' }}
											disabled={formState.values.beacon === undefined}
											onClick={(e) => _deleteField(
												e,
												'Beacon',
												'beacon',
												dropdowns.beacon.find(x => x.value === formState.values.beacon).text,
												formState.values.beacon
											)}
										>
											<CancelIcon />
										</IconButton>
									</span>
								</Tooltip>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
				<Accordion expanded={expanded === 'panel6'} onChange={handleChange('panel6')}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel6bh-content"
						id="panel6bh-header"
						className='highlight'
					>
						<Typography sx={{ width: '33%', flexShrink: 0 }}>Edit Unwind</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={1}>
							<Grid item md={3} xs={3}>
								<SelectField
									id="unwind"
									name="unwind"
									hintText="Unwind"
									defaultValue=""
									menuItems={(dropdowns && dropdowns.unwind) || []}
									value={formState.values.unwind || ''}
									onChange={_handleChangeDropDown}
								/>
							</Grid>
							<Grid item md={3} xs={3} style={{ display: 'flex', alignItems: 'center' }}>
								<Tooltip title="Add">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: '#1565c0' }}
											onClick={(e) => _addField(e, 'Unwind', 'unwind')}
										>
											<AddCircleOutlineIcon />
										</IconButton>
									</span>
								</Tooltip>
								<Tooltip title="Remove">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: formState.values.unwind === undefined ? 'grey' : 'red' }}
											disabled={formState.values.unwind === undefined}
											onClick={(e) => _deleteField(
												e,
												'Unwind',
												'unwind',
												dropdowns.unwind.find(x => x.value === formState.values.unwind).text,
												formState.values.unwind
											)}
										>
											<CancelIcon />
										</IconButton>
									</span>
								</Tooltip>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
				<Accordion expanded={expanded === 'panel7'} onChange={handleChange('panel7')}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel7bh-content"
						id="panel7bh-header"
						className='highlight'
					>
						<Typography sx={{ width: '33%', flexShrink: 0 }}>Edit MOCN Category Planned For Trial</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={1}>
							<Grid item md={5} xs={5}>
								<SelectField
									id="mocnCategoryPlannedForTrial"
									name="mocnCategoryPlannedForTrial"
									hintText="MOCN Category Planned For Trial"
									defaultValue=""
									menuItems={(dropdowns && dropdowns.mocnCategoryPlannedForTrial) || []}
									value={formState.values.mocnCategoryPlannedForTrial || ''}
									onChange={_handleChangeDropDown}
								/>
							</Grid>
							<Grid item md={3} xs={3} style={{ display: 'flex', alignItems: 'center' }}>
								<Tooltip title="Add">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: '#1565c0' }}
											onClick={(e) => _addField(e, 'MOCN Category Planned For Trial', 'mocnCategoryPlannedForTrial')}
										>
											<AddCircleOutlineIcon />
										</IconButton>
									</span>
								</Tooltip>
								<Tooltip title="Remove">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: formState.values.mocnCategoryPlannedForTrial === undefined ? 'grey' : 'red' }}
											disabled={formState.values.mocnCategoryPlannedForTrial === undefined}
											onClick={(e) => _deleteField(
												e,
												'MOCN Category Planned For Trial',
												'mocnCategoryPlannedForTrial',
												dropdowns.mocnCategoryPlannedForTrial.find(x => x.value === formState.values.mocnCategoryPlannedForTrial).text,
												formState.values.mocnCategoryPlannedForTrial
											)}
										>
											<CancelIcon />
										</IconButton>
									</span>
								</Tooltip>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
				<Accordion expanded={expanded === 'panel8'} onChange={handleChange('panel8')}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel8bh-content"
						id="panel8bh-header"
						className='highlight'
					>
						<Typography sx={{ width: '33%', flexShrink: 0 }}>Edit Script</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={1}>
							<Grid item md={3} xs={3}>
								<SelectField
									id="script"
									name="script"
									hintText="Script"
									defaultValue=""
									menuItems={(dropdowns && dropdowns.script) || []}
									value={formState.values.script || ''}
									onChange={_handleChangeDropDown}
								/>
							</Grid>
							<Grid item md={3} xs={3} style={{ display: 'flex', alignItems: 'center' }}>
								<Tooltip title="Add">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: '#1565c0' }}
											onClick={(e) => _addField(e, 'Script', 'script')}
										>
											<AddCircleOutlineIcon />
										</IconButton>
									</span>
								</Tooltip>
								<Tooltip title="Remove">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: formState.values.script === undefined ? 'grey' : 'red' }}
											disabled={formState.values.script === undefined}
											onClick={(e) => _deleteField(
												e,
												'Script',
												'script',
												dropdowns.script.find(x => x.value === formState.values.script).text,
												formState.values.script
											)}
										>
											<CancelIcon />
										</IconButton>
									</span>
								</Tooltip>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
				<Accordion expanded={expanded === 'panel9'} onChange={handleChange('panel9')}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel9bh-content"
						id="panel9bh-header"
						className='highlight'
					>
						<Typography sx={{ width: '33%', flexShrink: 0 }}>Edit Vendor</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={1}>
							<Grid item md={3} xs={3}>
								<SelectField
									id="vendor"
									name="vendor"
									hintText="Vendor"
									defaultValue=""
									menuItems={(dropdowns && dropdowns.vendor) || []}
									value={formState.values.vendor || ''}
									onChange={_handleChangeDropDown}
								/>
							</Grid>
							<Grid item md={3} xs={3} style={{ display: 'flex', alignItems: 'center' }}>
								<Tooltip title="Add">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: '#1565c0' }}
											onClick={(e) => _addField(e, 'Vendor', 'vendor')}
										>
											<AddCircleOutlineIcon />
										</IconButton>
									</span>
								</Tooltip>
								<Tooltip title="Remove">
									<span>
										<IconButton
											edge="end"
											size="small"
											style={{ color: formState.values.vendor === undefined ? 'grey' : 'red' }}
											disabled={formState.values.vendor === undefined}
											onClick={(e) => _deleteField(
												e,
												'Vendor',
												'vendor',
												dropdowns.vendor.find(x => x.value === formState.values.vendor).text,
												formState.values.vendor
											)}
										>
											<CancelIcon />
										</IconButton>
									</span>
								</Tooltip>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
			</div>
		</ModalDialog>
	);
};

SiteTrackerOptionsDialog.propTypes = {
	author: PropTypes.object,
	className: PropTypes.string,
	onApply: PropTypes.func,
	onClose: PropTypes.func,
	open: PropTypes.bool,
};

export default SiteTrackerOptionsDialog;
